import { X } from 'lucide-react'
import type { ChatSession } from '@/types/chat'
import { cn } from '@/lib/utils'

interface ChatTabBarProps {
  sessions: ChatSession[]
  activeSessionId: string | null
  onSelect: (sessionId: string) => void
  onClose: (sessionId: string) => void
  orientation?: 'horizontal' | 'vertical'
  className?: string
  compareMode?: boolean
  compareSelection?: string[]
  onToggleCompare?: (sessionId: string) => void
}

export function ChatTabBar({
  sessions,
  activeSessionId,
  onSelect,
  onClose,
  orientation = 'horizontal',
  className,
  compareMode = false,
  compareSelection = [],
  onToggleCompare,
}: ChatTabBarProps) {
  const isVertical = orientation === 'vertical'

  return (
    <div
      className={cn(
        isVertical
          ? 'flex h-full w-16 flex-col gap-1 border-r border-border bg-card p-1'
          : 'flex items-center gap-1 overflow-x-auto border-b border-border bg-card px-2 py-1',
        className,
      )}
      data-testid={isVertical ? 'chat-vertical-tabs' : 'chat-tab-bar'}
    >
      {sessions.map((session) => {
        const active = session.id === activeSessionId
        const selectedForCompare = compareSelection.includes(session.id)

        return (
          <div
            key={session.id}
            className={cn(
              'group flex items-center gap-1 rounded-md',
              isVertical ? 'flex-col px-1 py-2' : 'min-w-0 shrink-0 px-2 py-1',
              active ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
            )}
          >
            {compareMode ? (
              <input
                type="checkbox"
                checked={selectedForCompare}
                onChange={() => onToggleCompare?.(session.id)}
                aria-label={`Compare ${session.title}`}
                className="h-3.5 w-3.5 shrink-0"
                data-testid={`chat-compare-select-${session.id}`}
              />
            ) : null}
            <button
              type="button"
              onClick={() => onSelect(session.id)}
              className={cn('min-w-0 text-left text-xs font-medium', isVertical && 'text-center')}
              data-testid={`chat-tab-${session.id}`}
            >
              <span className={cn(isVertical ? 'block [writing-mode:vertical-rl] rotate-180' : 'truncate')}>
                {session.modelName}
              </span>
            </button>
            {!compareMode ? (
              <button
                type="button"
                aria-label={`Close ${session.title}`}
                onClick={() => onClose(session.id)}
                className={cn(
                  'rounded p-0.5 opacity-70 hover:opacity-100',
                  active ? 'hover:bg-primary-foreground/20' : 'hover:bg-muted-foreground/20',
                )}
                data-testid={`chat-tab-close-${session.id}`}
              >
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

interface ChatTabPickerProps {
  sessions: ChatSession[]
  activeSessionId: string | null
  onSelect: (sessionId: string) => void
  compareMode?: boolean
  compareSelection?: string[]
  onToggleCompare?: (sessionId: string) => void
}

export function ChatTabPicker({
  sessions,
  activeSessionId,
  onSelect,
  compareMode = false,
  compareSelection = [],
  onToggleCompare,
}: ChatTabPickerProps) {
  if (compareMode) {
    return (
      <div className="flex flex-col gap-1" data-testid="chat-tab-picker">
        {sessions.map((session) => (
          <label key={session.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={compareSelection.includes(session.id)}
              onChange={() => onToggleCompare?.(session.id)}
              data-testid={`chat-compare-select-${session.id}`}
            />
            {session.title}
          </label>
        ))}
      </div>
    )
  }

  return (
    <select
      className="rounded-md border border-input bg-background px-2 py-1 text-sm"
      value={activeSessionId ?? ''}
      onChange={(event) => onSelect(event.target.value)}
      data-testid="chat-tab-picker"
    >
      {sessions.map((session) => (
        <option key={session.id} value={session.id}>
          {session.title}
        </option>
      ))}
    </select>
  )
}
